import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Star, ThumbsUp, Meh, ThumbsDown, Tv, Film, Lock, Eye, Swords, Laugh, Heart, Ghost, Rocket, Search, Users, Clock, Music, Wand2, Mountain, Skull, Baby, Clapperboard, Newspaper, Drama, Sparkles, Map, Shield, MoreVertical, RefreshCw, Trash2, Check, ArrowRight, Loader2, Calendar, Plus, Mic, Info, Tag, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { AppHeader } from '../components/AppHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Genre icon and color mapping
const genreConfig = {
  'Action': { icon: Swords, color: 'bg-red-50 text-red-600 border-red-200' },
  'Adventure': { icon: Map, color: 'bg-amber-50 text-amber-600 border-amber-200' },
  'Animation': { icon: Sparkles, color: 'bg-purple-50 text-purple-600 border-purple-200' },
  'Comedy': { icon: Laugh, color: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
  'Crime': { icon: Search, color: 'bg-slate-50 text-slate-600 border-slate-200' },
  'Documentary': { icon: Newspaper, color: 'bg-blue-50 text-blue-600 border-blue-200' },
  'Drama': { icon: Drama, color: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
  'Family': { icon: Users, color: 'bg-green-50 text-green-600 border-green-200' },
  'Fantasy': { icon: Wand2, color: 'bg-violet-50 text-violet-600 border-violet-200' },
  'History': { icon: Clock, color: 'bg-orange-50 text-orange-600 border-orange-200' },
  'Horror': { icon: Ghost, color: 'bg-gray-50 text-gray-700 border-gray-300' },
  'Music': { icon: Music, color: 'bg-fuchsia-50 text-fuchsia-600 border-fuchsia-200' },
  'Mystery': { icon: Search, color: 'bg-cyan-50 text-cyan-600 border-cyan-200' },
  'Romance': { icon: Heart, color: 'bg-pink-50 text-pink-500 border-pink-200' },
  'Science Fiction': { icon: Rocket, color: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
  'Thriller': { icon: Skull, color: 'bg-neutral-50 text-neutral-700 border-neutral-300' },
  'TV Movie': { icon: Clapperboard, color: 'bg-slate-50 text-slate-600 border-slate-200' },
  'War': { icon: Shield, color: 'bg-stone-50 text-stone-600 border-stone-200' },
  'Western': { icon: Mountain, color: 'bg-amber-50 text-amber-700 border-amber-200' },
};

const getGenreConfig = (genre) => {
  return genreConfig[genre] || { icon: Film, color: 'bg-slate-50 text-slate-600 border-slate-200' };
};

export default function FilmsWatchedPage({ user }) {
  const navigate = useNavigate();
  const [watchedFilms, setWatchedFilms] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Genre filter state
  const [selectedGenre, setSelectedGenre] = useState(null);
  
  // Re-rank dialog state
  const [rerankingFilm, setRerankingFilm] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [isFirstWatch, setIsFirstWatch] = useState(false);
  const [isBigScreen, setIsBigScreen] = useState(false);
  const [watchDate, setWatchDate] = useState('');
  const [comment, setComment] = useState('');
  
  // Custom genre dialog state
  const [customGenreDialog, setCustomGenreDialog] = useState({ open: false, seriesId: null });
  const [customGenreInput, setCustomGenreInput] = useState('');
  
  // Head-to-head comparison state
  const [comparisonStep, setComparisonStep] = useState(false);
  const [existingItems, setExistingItems] = useState([]);
  const [comparisonItem, setComparisonItem] = useState(null);
  const [searchLow, setSearchLow] = useState(0);
  const [searchHigh, setSearchHigh] = useState(0);
  const [saving, setSaving] = useState(false);
  
  // View details state
  const [viewingDetails, setViewingDetails] = useState(null);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [watchedRes, statsRes] = await Promise.all([
        axios.get(`${API}/watched?media_type=movie`, { withCredentials: true }),
        axios.get(`${API}/watched/stats?media_type=movie`, { withCredentials: true })
      ]);
      
      setWatchedFilms(watchedRes.data.series || []);
      setStats(statsRes.data);
      
      // Auto-backfill genres if any items are missing them
      if (statsRes.data.missing_genres_count > 0) {
        try {
          await axios.post(`${API}/watched/backfill-genres`, {}, { withCredentials: true });
          // Refetch stats to update genre breakdown
          const refreshedStats = await axios.get(`${API}/watched/stats?media_type=movie`, { withCredentials: true });
          setStats(refreshedStats.data);
        } catch {
          // Genre backfill failed - items will display without genre icons
        }
      }
    } catch (error) {
      toast.error('Failed to load watched history');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRemoveFromWatched = async (film) => {
    if (!window.confirm(`Remove "${film.title}" from your watched list?`)) return;
    
    try {
      await axios.delete(`${API}/watched/${film.id}`, { withCredentials: true });
      toast.success('Removed from watched list');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove from watched list');
    }
  };
  
  const handleRerank = (film) => {
    setRerankingFilm(film);
    setSelectedGrade(film.grade);
    setIsFirstWatch(film.is_first_watch || false);
    setIsBigScreen(film.is_big_screen || false);
    setWatchDate(film.watch_date || '');
    setComment(film.comment || '');
    setComparisonStep(false);
    setExistingItems([]);
    setComparisonItem(null);
  };
  
  const handleProceedToRanking = async (grade) => {
    setSelectedGrade(grade);
    
    // Get all films with the same grade (excluding the one being re-ranked), sorted by rank
    const sameGradeFilms = watchedFilms
      .filter(f => f.grade === grade && f.id !== rerankingFilm.id)
      .sort((a, b) => a.rank_in_group - b.rank_in_group);
    
    if (sameGradeFilms.length === 0) {
      // No other films with this grade, save directly at rank 1
      await saveRerankedFilm(grade, 1);
    } else {
      // Start binary search comparison
      setExistingItems(sameGradeFilms);
      setSearchLow(0);
      setSearchHigh(sameGradeFilms.length);
      setComparisonStep(true);
      
      // Start with middle item
      const midIndex = Math.floor(sameGradeFilms.length / 2);
      setComparisonItem(sameGradeFilms[midIndex]);
    }
  };
  
  const handleComparisonChoice = async (preferReranking) => {
    let low = searchLow;
    let high = searchHigh;
    
    if (preferReranking) {
      // Re-ranking item is better, search upper half (lower ranks = better)
      high = existingItems.findIndex(item => item.id === comparisonItem.id);
    } else {
      // Comparison item is better, search lower half
      low = existingItems.findIndex(item => item.id === comparisonItem.id) + 1;
    }
    
    if (low >= high) {
      // Found the position
      const rank = low + 1; // Rank is 1-indexed
      await saveRerankedFilm(selectedGrade, rank);
    } else {
      // Continue binary search
      setSearchLow(low);
      setSearchHigh(high);
      const midIndex = Math.floor((low + high) / 2);
      setComparisonItem(existingItems[midIndex]);
    }
  };
  
  const saveRerankedFilm = async (grade, rankInGroup) => {
    setSaving(true);
    try {
      await axios.put(`${API}/watched/${rerankingFilm.id}`, {
        grade,
        rank_in_group: rankInGroup,
        is_first_watch: isFirstWatch,
        is_big_screen: isBigScreen,
        watch_date: watchDate || null,
        comment: comment || null
      }, { withCredentials: true });
      
      toast.success('Updated successfully');
      resetRerankDialog();
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };
  
  const handleUpdatePrimaryGenre = async (filmId, genre) => {
    try {
      await axios.put(`${API}/watched/${filmId}/primary-genre`, {
        primary_genre: genre
      }, { withCredentials: true });
      
      // Update local state
      setWatchedFilms(prev => prev.map(f => 
        f.id === filmId ? { ...f, primary_genre: genre } : f
      ));
      
      // Refresh stats to update genre counts
      const statsRes = await axios.get(`${API}/watched/stats?media_type=movie`, { withCredentials: true });
      setStats(statsRes.data);
      
      toast.success('Genre updated');
    } catch (error) {
      toast.error('Failed to update genre');
    }
  };
  
  const openCustomGenreDialog = (filmId) => {
    setCustomGenreDialog({ open: true, seriesId: filmId });
    setCustomGenreInput('');
  };
  
  const saveCustomGenre = async () => {
    if (!customGenreInput.trim() || !customGenreDialog.seriesId) return;
    await handleUpdatePrimaryGenre(customGenreDialog.seriesId, customGenreInput.trim());
    setCustomGenreDialog({ open: false, seriesId: null });
    setCustomGenreInput('');
  };
  
  const resetRerankDialog = () => {
    setRerankingFilm(null);
    setSelectedGrade(null);
    setComparisonStep(false);
    setExistingItems([]);
    setComparisonItem(null);
    setSearchLow(0);
    setSearchHigh(0);
    setIsFirstWatch(false);
    setIsBigScreen(false);
    setWatchDate('');
    setComment('');
  };
  
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Logout failed but we should still clear local state
    }
    localStorage.removeItem('auth_token');
    navigate('/');
  };
  
  const showScores = stats?.show_scores;
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#3D4F6F] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F87171] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#3D4F6F]">
      {/* Header with full navigation */}
      <AppHeader user={user} showBack={true} backPath="/films" />

      <main className="container mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/90 rounded-xl flex items-center justify-center shadow-lg">
              <Eye className="w-6 h-6 text-[#F87171]" />
            </div>
            <div>
              <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-white drop-shadow-lg">
                Films Watched
              </h1>
              <p className="text-white/80">
                {stats?.total_watched || 0} films watched and ranked
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/discover/films')}
            className="bg-white text-[#F87171] hover:bg-white/90 rounded-full"
            data-testid="add-watched-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Film
          </Button>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            label="Total Watched"
            value={stats?.total_watched || 0}
            icon={<Film className="w-5 h-5" />}
            color="coral"
          />
          <StatCard
            label="Liked"
            value={stats?.by_grade?.liked || 0}
            icon={<ThumbsUp className="w-5 h-5" />}
            color="green"
          />
          <StatCard
            label="Alright"
            value={stats?.by_grade?.alright || 0}
            icon={<Meh className="w-5 h-5" />}
            color="amber"
          />
          <StatCard
            label="Didn't Like"
            value={stats?.by_grade?.disliked || 0}
            icon={<ThumbsDown className="w-5 h-5" />}
            color="slate"
          />
        </div>
        
        {/* Genre Breakdown - Clickable Filters */}
        {stats?.by_genre && Object.keys(stats.by_genre).length > 0 && (
          <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm mb-6">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-outfit text-lg text-[#3D4F6F]">By Genre</CardTitle>
                {selectedGenre && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedGenre(null)}
                    className="text-xs text-[#64748B] hover:text-[#3D4F6F]"
                  >
                    Clear filter
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {Object.entries(stats.by_genre).map(([genre, count]) => {
                  const config = getGenreConfig(genre);
                  const IconComponent = config.icon;
                  const isSelected = selectedGenre === genre;
                  return (
                    <button 
                      key={genre} 
                      onClick={() => setSelectedGenre(isSelected ? null : genre)}
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-all hover:scale-105 text-left ${
                        isSelected 
                          ? 'ring-2 ring-[#F87171] ring-offset-2 ' + config.color
                          : config.color
                      }`}
                    >
                      <div className="flex-shrink-0">
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{genre}</p>
                        <p className="text-xs opacity-75">{count} {count === 1 ? 'film' : 'films'}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Score Unlock Progress */}
        {!showScores && (
          <Card className="mb-6 bg-white/95 backdrop-blur-sm border-0 shadow-xl" data-testid="score-unlock-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#F87171]/10 rounded-full flex items-center justify-center">
                  <Lock className="w-7 h-7 text-[#F87171]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-outfit font-semibold text-[#3D4F6F] text-lg" data-testid="score-unlock-title">
                    Unlock Your Film Scores
                  </h3>
                  <p className="text-[#64748B] text-sm" data-testid="score-unlock-message">
                    Watch {10 - (stats?.total_watched || 0)} more films to reveal your personalized scores
                  </p>
                  <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#F87171] rounded-full transition-all"
                      style={{ width: `${Math.min(((stats?.total_watched || 0) / 10) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Watched List - Unified Ranking */}
        {watchedFilms.length === 0 ? (
          <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 bg-[#F87171]/10 rounded-full flex items-center justify-center mb-4">
                <Film className="w-8 h-8 text-[#F87171]" />
              </div>
              <h3 className="font-outfit font-semibold text-[#3D4F6F] text-lg mb-2">
                No films watched yet
              </h3>
              <p className="text-[#64748B] text-center mb-6 max-w-sm">
                Start marking films as watched to build your ranking history.
              </p>
              <Button
                data-testid="go-to-discover-btn"
                onClick={() => navigate('/discover/films')}
                className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full"
              >
                Discover Films
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
            <CardHeader className="pb-2">
              <CardTitle className="font-outfit text-lg text-[#3D4F6F] flex items-center gap-2">
                <Star className="w-5 h-5 text-[#F87171]" />
                {selectedGenre 
                  ? `${selectedGenre} (${watchedFilms.filter(f => f && (f.primary_genre || f.genres?.[0]) === selectedGenre).length} films)`
                  : `My Rankings (${watchedFilms.length} films)`
                }
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {watchedFilms
                  .filter(film => film && film.tmdb_id)
                  .filter(film => !selectedGenre || (film.primary_genre || film.genres?.[0]) === selectedGenre)
                  .map((film, index) => (
                  <WatchedFilmCard 
                    key={film.id || film.tmdb_id || `film-${index}`} 
                    film={film} 
                    rank={index + 1} 
                    showScore={showScores}
                    onRemove={handleRemoveFromWatched}
                    onRerank={handleRerank}
                    onViewDetails={setViewingDetails}
                    getGenreConfig={getGenreConfig}
                    onUpdatePrimaryGenre={handleUpdatePrimaryGenre}
                    onOpenCustomGenre={openCustomGenreDialog}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
      
      {/* Re-rank Dialog */}
      <Dialog open={!!rerankingFilm} onOpenChange={() => !saving && resetRerankDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3D4F6F]">
              {comparisonStep ? 'Which do you prefer?' : 'Re-rank Film'}
            </DialogTitle>
            <DialogDescription className="text-[#64748B]">
              {comparisonStep 
                ? 'Click the film you prefer to continue ranking'
                : `Update your rating for "${rerankingFilm?.title}"`
              }
            </DialogDescription>
          </DialogHeader>
          
          {!comparisonStep ? (
            <div className="space-y-4 py-4">
              {/* Grade Selection */}
              <div className="space-y-2">
                {[
                  { g: 'liked', icon: <ThumbsUp className="w-6 h-6" />, label: 'I liked it', cls: 'border-[#F87171] bg-[#F87171]/5 text-[#F87171]' },
                  { g: 'alright', icon: <Meh className="w-6 h-6" />, label: 'It was alright', cls: 'border-amber-400 bg-amber-50 text-amber-600' },
                  { g: 'disliked', icon: <ThumbsDown className="w-6 h-6" />, label: "I didn't like it", cls: 'border-slate-400 bg-slate-50 text-slate-600' }
                ].map(({ g, icon, label, cls }) => (
                  <button 
                    key={g} 
                    onClick={() => setSelectedGrade(g)} 
                    className={`w-full flex items-center gap-4 p-3 rounded-xl border-2 transition-all ${selectedGrade === g ? cls : 'border-slate-200 hover:border-slate-300'}`}
                  >
                    <div className={selectedGrade === g ? '' : 'text-slate-400'}>{icon}</div>
                    <span className={`font-medium ${selectedGrade === g ? '' : 'text-[#3D4F6F]'}`}>{label}</span>
                    {selectedGrade === g && <Check className="w-5 h-5 ml-auto" />}
                  </button>
                ))}
              </div>
              
              {/* First Time Watch & Big Screen Toggles - Horizontal */}
              <div className="pt-2 border-t border-slate-100 grid grid-cols-2 gap-2">
                <button
                  onClick={() => setIsFirstWatch(!isFirstWatch)}
                  className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                    isFirstWatch 
                      ? 'border-purple-400 bg-purple-50 text-purple-700' 
                      : 'border-slate-200 hover:border-slate-300 text-[#64748B]'
                  }`}
                >
                  <Eye className={`w-5 h-5 ${isFirstWatch ? 'text-purple-600' : 'text-slate-400'}`} />
                  <span className="font-medium text-sm">First watch</span>
                </button>
                <button
                  onClick={() => setIsBigScreen(!isBigScreen)}
                  className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                    isBigScreen 
                      ? 'border-amber-400 bg-amber-50 text-amber-700' 
                      : 'border-slate-200 hover:border-slate-300 text-[#64748B]'
                  }`}
                >
                  <Clapperboard className={`w-5 h-5 ${isBigScreen ? 'text-amber-600' : 'text-slate-400'}`} />
                  <span className="font-medium text-sm">Big screen</span>
                </button>
              </div>
              
              {/* Watch Date */}
              <div className="pt-2">
                <label className="text-sm font-medium text-[#64748B] mb-2 block flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Date watched
                </label>
                <input
                  type="date"
                  value={watchDate}
                  onChange={(e) => setWatchDate(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F87171]/50"
                />
              </div>
              
              {/* Comments/Thoughts */}
              <div className="pt-2">
                <label className="text-sm font-medium text-[#64748B] mb-2 block">
                  Thoughts (optional)
                </label>
                <Textarea
                  placeholder="What did you think about it?"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="resize-none h-20"
                />
              </div>
              
              <DialogFooter className="pt-2">
                <Button
                  onClick={() => handleProceedToRanking(selectedGrade)}
                  disabled={!selectedGrade || saving}
                  className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full w-full"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Continue to Ranking
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="py-4">
              {/* Head-to-head comparison */}
              <p className="text-center text-[#64748B] mb-4">
                Which do you prefer?
              </p>
              <div className="grid grid-cols-2 gap-4">
                {/* Re-ranking film */}
                <button
                  onClick={() => handleComparisonChoice(true)}
                  disabled={saving}
                  className="p-4 rounded-xl border-2 border-slate-200 hover:border-[#F87171] hover:bg-[#F87171]/5 transition-all text-center"
                >
                  <div className="w-24 h-36 mx-auto rounded-lg overflow-hidden mb-3 shadow-md">
                    {rerankingFilm?.poster_path ? (
                      <img src={rerankingFilm.poster_path} alt={rerankingFilm.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-slate-200 flex items-center justify-center">
                        <Film className="w-8 h-8 text-slate-400" />
                      </div>
                    )}
                  </div>
                  <p className="font-medium text-[#3D4F6F] text-sm truncate">{rerankingFilm?.title}</p>
                </button>
                
                {/* Comparison item */}
                <button
                  onClick={() => handleComparisonChoice(false)}
                  disabled={saving}
                  className="p-4 rounded-xl border-2 border-slate-200 hover:border-[#F87171] hover:bg-[#F87171]/5 transition-all text-center"
                >
                  <div className="w-24 h-36 mx-auto rounded-lg overflow-hidden mb-3 shadow-md">
                    {comparisonItem?.poster_path ? (
                      <img src={comparisonItem.poster_path} alt={comparisonItem.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-slate-200 flex items-center justify-center">
                        <Film className="w-8 h-8 text-slate-400" />
                      </div>
                    )}
                  </div>
                  <p className="font-medium text-[#3D4F6F] text-sm truncate">{comparisonItem?.title}</p>
                </button>
              </div>
              
              {saving && (
                <div className="flex items-center justify-center mt-4">
                  <Loader2 className="w-5 h-5 animate-spin text-[#F87171]" />
                  <span className="ml-2 text-[#64748B]">Saving...</span>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Details Dialog - Mobile view */}
      <Dialog open={!!viewingDetails} onOpenChange={() => setViewingDetails(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3D4F6F]">{viewingDetails?.title}</DialogTitle>
          </DialogHeader>
          {viewingDetails && (
            <div className="space-y-4">
              <div className="flex gap-4">
                {viewingDetails.poster_path && (
                  <img 
                    src={viewingDetails.poster_path} 
                    alt={viewingDetails.title}
                    className="w-24 h-36 object-cover rounded-lg shadow"
                  />
                )}
                <div className="flex-1 space-y-2">
                  {viewingDetails.first_air_date && (
                    <p className="text-sm text-[#64748B]">
                      Year: {(() => {
                        try {
                          return new Date(viewingDetails.first_air_date).getFullYear();
                        } catch {
                          return 'Unknown';
                        }
                      })()}
                    </p>
                  )}
                  {viewingDetails.watch_date && (
                    <p className="text-sm text-[#64748B] flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      Watched: {(() => {
                        try {
                          return new Date(viewingDetails.watch_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
                        } catch {
                          return 'Unknown';
                        }
                      })()}
                    </p>
                  )}
                  {viewingDetails.is_first_watch && (
                    <p className="text-sm text-purple-600 flex items-center gap-1">
                      <Eye className="w-3.5 h-3.5" />
                      First time watch
                    </p>
                  )}
                  {viewingDetails.is_big_screen && (
                    <p className="text-sm text-amber-600 flex items-center gap-1">
                      <Clapperboard className="w-3.5 h-3.5" />
                      Watched in cinema
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#64748B]">Rating:</span>
                    {viewingDetails.grade === 'liked' && <span className="text-green-600 flex items-center gap-1"><ThumbsUp className="w-4 h-4"/> Liked</span>}
                    {viewingDetails.grade === 'alright' && <span className="text-amber-600 flex items-center gap-1"><Meh className="w-4 h-4"/> Alright</span>}
                    {viewingDetails.grade === 'disliked' && <span className="text-slate-600 flex items-center gap-1"><ThumbsDown className="w-4 h-4"/> Disliked</span>}
                  </div>
                </div>
              </div>
              {viewingDetails.comment && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-[#64748B] mb-1">Your thoughts:</p>
                  <p className="text-sm text-[#3D4F6F] italic">&ldquo;{viewingDetails.comment}&rdquo;</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Custom Genre Dialog */}
      <Dialog open={customGenreDialog.open} onOpenChange={(open) => !open && setCustomGenreDialog({ open: false, seriesId: null })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#3D4F6F]">Enter Custom Genre</DialogTitle>
            <DialogDescription>
              Enter your own genre for this film
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="e.g., Dark Comedy, Sci-Fi Thriller..."
            value={customGenreInput}
            onChange={(e) => setCustomGenreInput(e.target.value)}
            className="mt-2"
            data-testid="custom-genre-input"
            onKeyDown={(e) => e.key === 'Enter' && saveCustomGenre()}
          />
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCustomGenreDialog({ open: false, seriesId: null })}>
              Cancel
            </Button>
            <Button 
              onClick={saveCustomGenre}
              disabled={!customGenreInput.trim()}
              className="bg-purple-500 hover:bg-purple-600"
            >
              Save Genre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, icon, color = "default" }) {
  const colorClasses = {
    default: "bg-white/95 border-white/50",
    coral: "bg-white/95 border-[#F87171]/30",
    green: "bg-white/95 border-green-200",
    amber: "bg-white/95 border-amber-200",
    slate: "bg-white/95 border-slate-200",
  };
  
  const iconColors = {
    default: "text-[#64748B]",
    coral: "text-[#F87171]",
    green: "text-green-600",
    amber: "text-amber-500",
    slate: "text-slate-500",
  };
  
  return (
    <div className={`rounded-2xl border p-4 backdrop-blur-sm shadow-lg ${colorClasses[color]}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[#64748B] text-sm">{label}</span>
        <span className={iconColors[color]}>{icon}</span>
      </div>
      <p className="font-outfit font-bold text-2xl text-[#3D4F6F]">{value}</p>
    </div>
  );
}

function WatchedFilmCard({ film, rank, showScore, onRemove, onRerank, onViewDetails, getGenreConfig, onUpdatePrimaryGenre, onOpenCustomGenre }) {
  // Defensive check - if film is null/undefined, don't render
  if (!film) return null;
  
  const gradeConfig = {
    liked: { icon: ThumbsUp, color: 'bg-green-100 text-green-700' },
    alright: { icon: Meh, color: 'bg-amber-100 text-amber-700' },
    disliked: { icon: ThumbsDown, color: 'bg-slate-100 text-slate-600' }
  };
  
  const grade = gradeConfig[film.grade] || gradeConfig.alright;
  const GradeIcon = grade.icon;
  
  // Get primary genre (use primary_genre if set, otherwise first in array)
  const primaryGenre = film.primary_genre || film.genres?.[0];
  const genreInfo = primaryGenre && getGenreConfig ? getGenreConfig(primaryGenre) : null;
  const GenreIcon = genreInfo?.icon || Film;
  const hasMultipleGenres = film.genres?.length > 1;
  
  return (
    <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors group">
      {/* Rank */}
      <span className="font-outfit font-bold text-lg sm:text-xl text-[#3D4F6F] w-6 sm:w-8 text-center flex-shrink-0 pt-1">
        {rank}
      </span>
      
      {/* Poster - Clickable */}
      <div 
        className="w-10 h-14 sm:w-12 sm:h-16 rounded-lg overflow-hidden flex-shrink-0 shadow-sm cursor-pointer hover:scale-105 transition-transform"
        onClick={() => onViewDetails?.(film)}
      >
        {film.poster_path ? (
          <img src={film.poster_path} alt={film.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-slate-200 flex items-center justify-center">
            <Film className="w-4 h-4 text-slate-400" />
          </div>
        )}
      </div>
      
      {/* Content - Two Line Layout */}
      <div className="flex-1 min-w-0">
        {/* Line 1: Title + Score */}
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => onViewDetails?.(film)}>
          <h4 className="font-medium text-sm sm:text-base text-[#3D4F6F] truncate flex-1">{film.title}</h4>
          {/* Score badge */}
          {showScore && film.calculated_score !== undefined && film.calculated_score !== null && (
            <div className="flex items-center gap-0.5 bg-amber-50 px-1.5 py-0.5 rounded-full flex-shrink-0" data-testid="score-display">
              <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
              <span className="font-medium text-xs text-amber-700">{Number(film.calculated_score).toFixed(1)}</span>
            </div>
          )}
        </div>
        
        {/* Line 2: Date on left, Icons on right */}
        <div className="flex items-center justify-between mt-1">
          {/* Left: Release date/year */}
          <div className="flex items-center gap-1 text-xs text-[#94A3B8]">
            {film.first_air_date && (
              <span>{(() => { try { return new Date(film.first_air_date).getFullYear(); } catch { return ''; } })()}</span>
            )}
            {film.watch_date && (
              <span className="hidden sm:flex items-center gap-1">
                <span className="mx-1">•</span>
                <Calendar className="w-3 h-3" />
                {(() => {
                  try {
                    return new Date(film.watch_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                  } catch {
                    return '';
                  }
                })()}
              </span>
            )}
          </div>
          
          {/* Right: Icons */}
                          <div className="flex items-center gap-1">
                            {primaryGenre && (
                              <div className={`p-1 rounded ${genreInfo?.color || 'bg-slate-100 text-slate-600'}`} title={primaryGenre}>
                                <GenreIcon className="w-3 h-3" />
                              </div>
                            )}
                            <div className={`p-1 rounded-full ${grade.color}`}>
                              <GradeIcon className="w-3 h-3" />
                            </div>
                          </div>
        </div>
        
        {/* Comment preview on desktop */}
        {film.comment && (
          <p className="hidden sm:block text-xs text-[#94A3B8] truncate mt-0.5 italic">&ldquo;{film.comment}&rdquo;</p>
        )}
      </div>
      
      {/* Actions Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon"
            className="h-8 w-8 p-0 text-[#64748B] hover:text-[#3D4F6F] flex-shrink-0"
          >
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onViewDetails?.(film)} className="cursor-pointer sm:hidden">
            <Info className="w-4 h-4 mr-2 text-blue-600" />
            View Details
          </DropdownMenuItem>
          {/* Change Genre option - always show since we have custom option */}
          {onUpdatePrimaryGenre && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="flex items-center px-2 py-1.5 text-sm cursor-pointer hover:bg-slate-100 rounded-sm">
                  <Tag className="w-4 h-4 mr-2 text-[#F87171]" />
                  Change Genre
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="left" align="start" className="min-w-[140px]">
                {(film.genres || []).map((genre) => {
                  const gInfo = getGenreConfig ? getGenreConfig(genre) : null;
                  const GIcon = gInfo?.icon || Film;
                  const isSelected = genre === primaryGenre;
                  return (
                    <DropdownMenuItem 
                      key={genre}
                      onClick={() => onUpdatePrimaryGenre(film.id, genre)}
                      className={`cursor-pointer ${isSelected ? 'bg-[#F87171]/10' : ''}`}
                    >
                      <GIcon className={`w-4 h-4 mr-2 ${gInfo?.color?.split(' ')[1] || 'text-slate-600'}`} />
                      {genre}
                      {isSelected && <Check className="w-4 h-4 ml-auto text-[#F87171]" />}
                    </DropdownMenuItem>
                  );
                })}
                {/* Custom Genre Option */}
                <DropdownMenuItem 
                  onClick={() => onOpenCustomGenre?.(film.id)}
                  className="cursor-pointer text-purple-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Custom Genre...
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <DropdownMenuItem onClick={() => onRerank?.(film)} className="cursor-pointer">
            <RefreshCw className="w-4 h-4 mr-2 text-purple-600" />
            Re-rank
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onRemove?.(film)} className="cursor-pointer text-red-600">
            <Trash2 className="w-4 h-4 mr-2" />
            Remove
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
